import { useState, type FormEvent } from "react";
import Reveal from "./Reveal";

const topics = [
  "Managed IT Services",
  "Cybersecurity",
  "Cloud Solutions",
  "Backup & Disaster Recovery",
  "Business VoIP",
  "Network Infrastructure",
  "IT Consulting",
  "AI & Emerging Technology",
  "Other / Not sure yet",
];

type Status = "idle" | "submitting" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/dahub5boh653ivfvo5n0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";

      if (res.ok && code === "OK") {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setFormError("Network error. Please try again.");
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-white px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-shadow";

  return (
    <section id="contact" className="py-20 md:py-28 bg-background-50 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <Reveal className="max-w-2xl mx-auto text-center">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
            Contact Us
          </span>
          <h2 className="mt-3 font-heading font-extrabold text-foreground-950 text-3xl md:text-4xl tracking-tight">
            Let's Talk Technology
          </h2>
          <p className="mt-4 text-foreground-600 text-base md:text-lg leading-relaxed">
            Have an IT challenge, need better security, or looking for a reliable technology
            partner? Get in touch with us.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Contact info */}
          <Reveal className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-white p-7 h-full flex flex-col">
              <div className="flex items-center gap-3 pb-5 border-b border-background-200">
                <span className="w-12 h-12 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center">
                  <i className="ri-user-line text-xl" />
                </span>
                <div>
                  <p className="font-heading font-bold text-foreground-950">Nicolas Mancini</p>
                  <p className="text-sm text-foreground-500">Owner</p>
                </div>
              </div>

              <div className="mt-6 space-y-5">
                <a
                  href="mailto:nick@thetechconsultantsllc.com"
                  className="flex items-center gap-3 group cursor-pointer"
                >
                  <span className="w-11 h-11 rounded-md bg-background-100 text-foreground-700 flex items-center justify-center group-hover:bg-primary-50 group-hover:text-primary-600 transition-colors">
                    <i className="ri-mail-line text-lg" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-foreground-500 font-semibold">
                      Email
                    </p>
                    <p className="text-sm text-foreground-900 font-medium">
                      nick@thetechconsultantsllc.com
                    </p>
                  </div>
                </a>

                <a href="tel:+18184696734" className="flex items-center gap-3 group cursor-pointer">
                  <span className="w-11 h-11 rounded-md bg-background-100 text-foreground-700 flex items-center justify-center group-hover:bg-primary-50 group-hover:text-primary-600 transition-colors">
                    <i className="ri-phone-line text-lg" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-foreground-500 font-semibold">
                      Phone
                    </p>
                    <p className="text-sm text-foreground-900 font-medium">(818) 469-6734</p>
                  </div>
                </a>
              </div>

              <div className="mt-auto pt-6">
                <div className="rounded-md bg-primary-50 p-5">
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    <i className="ri-information-line text-primary-600 mr-1.5" />
                    Reach out for a free consultation. We'll respond promptly to discuss your
                    technology needs.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={100} className="lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="rounded-lg border border-background-200 bg-white p-7"
              noValidate
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    Full Name
                  </label>
                  <input id="name" name="name" type="text" required placeholder="John Smith" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    Company
                  </label>
                  <input id="company" name="company" type="text" placeholder="Your company" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    Email
                  </label>
                  <input id="email" name="email" type="email" required placeholder="you@company.com" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    Phone
                  </label>
                  <input id="phone" name="phone" type="tel" placeholder="(555) 000-0000" className={inputClass} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="topic" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    How Can We Help?
                  </label>
                  <select id="topic" name="topic" className={inputClass} defaultValue="Managed IT Services">
                    {topics.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    required
                    placeholder="Tell us about your technology needs or challenges..."
                    className={`${inputClass} resize-none`}
                  />
                </div>
              </div>

              {/* Anti-spam field */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="offscreen-field"
              />

              <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 text-white px-7 py-3.5 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200 cursor-pointer"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <i className="ri-send-plane-line" />
                    </>
                  )}
                </button>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  Your information is kept private and will only be used to respond to your inquiry.
                </p>
              </div>

              {status === "success" && (
                <div className="mt-5 rounded-md bg-accent-50 border border-accent-200 px-4 py-3 text-sm text-accent-900">
                  <i className="ri-checkbox-circle-line mr-1.5" />
                  Thank you! Your message has been sent. We'll be in touch shortly.
                </div>
              )}

              {status === "error" && (
                <div className="mt-5 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                  <i className="ri-error-warning-line mr-1.5" />
                  {formError || "Something went wrong. Please try again."}
                </div>
              )}
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}